import axios from 'axios';

const API_BASE_URL = 'https://codeforgeai.zbackend.workers.dev';
// const API_BASE_URL = 'http://127.0.0.1:8787';
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});


// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (username, password) =>
    api.post('/api/auth/login', { username, password }),

  register: (username, email, password) =>
    api.post('/api/auth/register', { username, email, password }),
};

export const projectsAPI = {
  getAll: () =>
    api.get('/api/projects/')
      .then(response => {
        console.log('Projects data:', response.data);
        return response.data;
      })
      .catch(error => {
        console.error('Error fetching projects:', error);
        throw error;
      }),

  getById: (id) =>
    api.get(`/api/projects/${id}`)
      .then(response => response.data)
      .catch(error => {
        console.error(`Error fetching project ${id}:`, error);
        throw error;
      }),

  getVersion: (projectId, versionId) =>
  api.get(`/api/projects/${projectId}/versions/${versionId}`)
    .then(response => {
      console.log('Version API Response:', response.data);
      return response.data; // ✅ Return the data directly
    })
    .catch(error => {
      console.error(`Error fetching version ${versionId} for project ${projectId}:`, error);
      throw error;
    }),

  delete: (id) => api.delete(`/api/projects/${id}`),

  // Original non-streaming method (keep for compatibility)
  generate: (data) => api.post('/api/generate/project', data),
   generateStreamingV2: async (formData, onProgress) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/generate/project/strea`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          streaming: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6);
            if (data === '[DONE]') break;

            try {
              const progress = JSON.parse(data);
              onProgress(progress);
            } catch (e) {
              console.error('Error parsing progress:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Streaming error:', error);
      onProgress({
        type: 'error',
        error: error.message
      });
    }
  },
    // NEW: Streaming method
  generateStreaming: async (data, onProgress) => {
    try {
      const token = localStorage.getItem('token');

      console.log('🔵 [API] Starting streaming request...');
      console.log('🔵 [API] URL:', `${API_BASE_URL}/api/generate/project/stream`);
      console.log('🔵 [API] Data:', data);

      const response = await fetch(`${API_BASE_URL}/api/generate/project/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(data),
      });

      console.log('🔵 [API] Response status:', response.status);
      console.log('🔵 [API] Response OK:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 [API] Streaming request failed:', response.status, errorText);
        throw new Error(`Streaming request failed: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      console.log('🟢 [API] Starting to read stream...');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('🟢 [API] Stream completed');
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();

            if (!trimmedLine || trimmedLine === 'data: [DONE]') continue;

            if (trimmedLine.startsWith('data: ')) {
              try {
                const jsonData = JSON.parse(trimmedLine.slice(6));
                console.log('🟡 [API] Progress update:', jsonData);
                onProgress(jsonData);
              } catch (e) {
                console.debug('🔴 [API] Failed to parse streaming chunk:', e, 'Line:', trimmedLine);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      console.log('🟢 [API] Streaming finished successfully');
      return { data: { success: true, streaming_complete: true } };

    } catch (error) {
      console.error('🔴 [API] Streaming error:', error);
      throw error;
    }
},

  refactor: (projectId, prompt) =>
    api.post(`/api/generate/project/${projectId}/refactor`, { prompt }),

  download: (projectId) =>
    api.get(`/api/generate/project/${projectId}/download`, {
      responseType: 'blob'
    }),

  async updateProjectFiles(projectId, fileChanges) {
    console.log('🔄 [API] updateProjectFiles - getting token...');

    // 🚨 FIX: Get from 'token' key, not 'auth_token'
    const token = localStorage.getItem('token');

    console.log('🔐 Token from localStorage.getItem("token"):', token ? `${token.length} chars` : 'NOT FOUND');
    console.log('🔐 Token preview:', token ? `${token.substring(0, 50)}...` : 'none');

    if (!token) {
      console.error('❌ No token found in localStorage.getItem("token")');
      throw new Error('You need to be logged in to save changes');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    console.log('📤 Sending PUT request to:', `${API_BASE_URL}/api/projects/${projectId}/files`);
    console.log('📤 Headers:', headers);

    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ fileChanges })
      });

      console.log('📥 Response status:', response.status);
      console.log('📥 Response OK:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);

        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        throw new Error(`Failed to save changes: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ Success:', result);
      return result;

    } catch (error) {
      console.error('❌ Request failed:', error);
      throw error;
    }
  }
};

export default api;
